import React from 'react';
import {
  describeHistoryEntry,
  historyEntryDate,
  sortedHistoryEntries,
  TEXT_CHANGE_TYPES,
} from '../../utils/letterParagraphHistory';
import { describeOverrides, EVIDENCE_OVERRIDE_LABELS, overrideState } from '../../utils/evidenceOverrides';

const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function formatWhen(entry) {
  const date = historyEntryDate(entry);
  return date ? DATE_FORMAT.format(date) : 'Time not recorded';
}

/**
 * The history of one letter paragraph (TDI-408): every change, newest first,
 * with who made it, when, and how. AI revisions show the instruction they
 * were made from; changes of text can be opened to read the before and after.
 * Read-only; the record is the source and this view never writes to it.
 */
export function ParagraphHistory({ history, paragraphTitle }) {
  const entries = sortedHistoryEntries(history).reverse();

  return (
    <section className="p-4 pt-0" aria-label="History of this paragraph" data-testid="paragraph-history">
      <h3 className="text-base font-semibold text-gray-900 text-left">History of this paragraph</h3>
      <p className="mt-1 mb-3 text-sm text-gray-600 text-left">
        Every change to {paragraphTitle ? `"${paragraphTitle}"` : 'this paragraph'}, newest first.
        It stays with the letter after sign-off.
      </p>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500 text-left">No changes yet. This is the AI draft as generated.</p>
      ) : (
        <ol className="space-y-2">
          {entries.map((entry) => {
            const editType = String(entry.editType || '');
            const isTextChange = TEXT_CHANGE_TYPES.has(editType);
            return (
              <li key={entry.key} className="rounded-lg border border-gray-200 bg-white p-3 text-left">
                <p className="text-sm font-medium text-gray-900">{describeHistoryEntry(entry)}</p>
                <p className="mt-0.5 text-xs text-gray-600">
                  {entry.clinicianName || 'Unknown user'} · {formatWhen(entry)}
                </p>
                {entry.clearedApproval && (
                  <p className="mt-1 text-xs text-amber-800">
                    This change cleared the paragraph&apos;s earlier approval.
                  </p>
                )}
                {editType === 'suggest_changes' && entry.instruction && (
                  <p className="mt-2 text-sm text-gray-800">
                    Instruction: <span className="italic">&quot;{entry.instruction}&quot;</span>
                  </p>
                )}
                {editType === 'evidence_change' && entry.evidenceOverrides && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-primary">
                      Evidence adjusted: {describeOverrides(entry.evidenceOverrides) || 'none recorded'}
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {Object.keys(entry.evidenceOverrides).map((key) => {
                        const state = overrideState(entry.evidenceOverrides, key);
                        if (!state) return null;
                        return (
                          <li key={key} className="text-xs text-gray-800">
                            <span className="font-semibold">{EVIDENCE_OVERRIDE_LABELS[state]}:</span>{' '}
                            {entry.evidenceOverrides[key].text}
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                )}
                {isTextChange && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-primary">Show the text before and after</summary>
                    <p className="mt-2 text-xs font-semibold text-gray-500">Before</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.previousValue || '(empty)'}</p>
                    <p className="mt-2 text-xs font-semibold text-gray-500">After</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.newValue || '(empty)'}</p>
                  </details>
                )}
                {editType === 'approved' && entry.approvedText && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-primary">Show the text approved</summary>
                    <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{entry.approvedText}</p>
                  </details>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

export default ParagraphHistory;
